"use client"

import React, { useActionState, useEffect } from "react"
import Form from "next/form"
import { useRouter } from "next/navigation"
import { updateEmailAction, UpdateEmailActionState } from "@/actions/profileActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { LoaderCircle, PlusIcon, SaveIcon } from "lucide-react"
import { useForm } from "react-hook-form"

import { hasEntries } from "@/lib/helper"
import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { zodResolver } from "@/lib/polyfill/zodResolver"
import { AccountContext } from "@/lib/storefront/account/account-service"
import { cn } from "@/lib/utils"
import { Button, Input } from "@/components/ui/components"
import { PasswordToggleInput } from "@/components/ui/components/password-toggle-input"
import AccountBackLinkClient from "@/components/account/AccountBackLinkClient"
import { UpdateEmailForm, UpdateEmailSchema } from "@/components/account/profile/profile"
import { FormRow } from "@/components/account/shared/FormRow"
import { UbAccountProfileEmailViewStoryblok } from "@/components/storyblok/component-types"
import UbRichText from "@/components/storyblok/UbRichText"

const resolver = zodResolver(UpdateEmailSchema)

interface AccountProfileEmailProps {
  accountContext: AccountContext
  blok: UbAccountProfileEmailViewStoryblok
}

export default function AccountProfileEmailClient(props: AccountProfileEmailProps) {
  const { setProfileUpdated } = useUbStorefrontState()
  const router = useRouter()

  async function action(initialState: UpdateEmailActionState, formData: FormData) {
    return updateEmailAction(initialState, formData, props.accountContext.customerData?.customerType).then(
      async (state) => {
        if (!hasEntries(state.errors)) {
          setProfileUpdated(true)
          router.push(getLinkWithPrefixFromURLAndLocale("/account/profile", props.accountContext.sbLocale))
        }
        return {
          values: state.values,
          errors: state.errors,
        }
      }
    )
  }

  const [state, formAction, isPending] = useActionState(action, {
    values: {
      currentEmail: props.accountContext.userData?.email ?? "",
      currentPassword: "",
      email: "",
      confirmEmail: "",
    },
    errors: {},
  })

  const { formState, register, reset } = useForm<UpdateEmailForm>({
    values: state.values,
    errors: state.errors,
    resetOptions: {
      keepErrors: true,
    },
    mode: "onChange",
    resolver,
  })

  useEffect(() => {
    reset(state.values, {
      keepErrors: true,
    })
  }, [reset, state.values])

  return (
    <>
      <AccountBackLinkClient label={props.blok.label_common_back} />
      <div className={"relative flex w-full flex-col gap-2 lg:gap-6"}>
        <div className={"flex flex-col gap-2"}>
          <h2 className={"text-lg font-bold"}>{props.blok.title}</h2>
          <p>{props.blok.description}</p>
          <p className={"text-xs"}>{props.blok.required}</p>
        </div>
        <div>
          <Form action={formAction}>
            <div className="flex flex-col gap-2 lg:gap-6">
              <FormRow htmlFor="currentEmail" label={props.blok.current_email} required>
                <Input
                  readOnly={true}
                  {...register("currentEmail")}
                  className={cn("", { "border-primary": formState.errors.currentEmail })}
                />
              </FormRow>

              <FormRow label={props.blok.email} htmlFor={"email"} required>
                <Input {...register("email")} className={cn("", { "border-primary": formState.errors.email })} />
                {formState.errors.email && (
                  <p className={"mt-1 text-xs text-red-600"} role="alert">
                    {formState.errors.email.type === "notUnique"
                      ? props.blok.email_not_unique
                      : props.blok.email_invalid}
                  </p>
                )}
              </FormRow>

              <FormRow label={props.blok.confirm_email} htmlFor={"confirmEmail"} required>
                <Input
                  {...register("confirmEmail")}
                  className={cn("", { "border-primary": formState.errors.confirmEmail })}
                />
                {formState.errors.confirmEmail && (
                  <p className={"mt-1 text-xs text-red-600"} role="alert">
                    {formState.errors.confirmEmail.message === "notMatch"
                      ? props.blok.email_not_match
                      : props.blok.email_invalid}
                  </p>
                )}
              </FormRow>

              <FormRow label={props.blok.password} htmlFor={"currentPassword"} required>
                <PasswordToggleInput
                  autoComplete="current-password"
                  {...register("currentPassword")}
                  className={cn("", { "border-primary": formState.errors.currentPassword })}
                />
                {formState.errors.currentPassword && (
                  <p className={"mt-1 text-xs text-red-600"} role="alert">
                    {formState.errors.currentPassword.message === "invalid"
                      ? props.blok.password_invalid
                      : props.blok.password_missing}
                  </p>
                )}
              </FormRow>
              <div className="flex flex-wrap justify-between gap-2 lg:justify-end">
                <Button type={"submit"} size={"defaultNoGrow"} disabled={isPending} className={"gap-1"}>
                  {!isPending ? (
                    <SaveIcon className={"inline size-5"} />
                  ) : (
                    <LoaderCircle className={"inline size-5 animate-spin"} />
                  )}
                  <span>{props.blok.submit}</span>
                </Button>

                <Button
                  type={"reset"}
                  variant="outline"
                  size={"defaultNoGrow"}
                  className={"gap-1"}
                  onClick={() => router.back()}
                >
                  <PlusIcon className={"inline size-5 rotate-45"} />
                  <span>{props.blok.cancel}</span>
                </Button>
              </div>
            </div>
          </Form>
        </div>
        {props.blok.help && (
          <div>
            <UbRichText blok={{ content: props.blok.help, _uid: "", component: "ub_rich_text" }} />
          </div>
        )}
      </div>
    </>
  )
}
